import React from 'react';
import PropTypes from 'prop-types';

interface TreegridProps extends React.HTMLAttributes<HTMLElement> {
    isCompact: boolean;
    columnSizes: Array<'16' | '24' | '32' | '40' | '48' | 'x-small' | 'small' | 'medium' | 'wide' | 'x-wide' | 'xx-wide' | 'xxx-wide' | 'fill'>;
    headings?: Array<string>;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
}

/**
 * A chart layout that is styled to allow for grid-based layouts.
 * 
 * ## _
 * ### Arguments
 * @param {boolean} isCompact - A flag to set the treegrid to be compact.
 * @param {Array<string>} columnSizes - A list of all the column sizes this treegrid contains. This should be an array of the strings listed below:
 * - Available sizes (in pixels): `16`, `24`, `32`, `40`, `48`
 * - Available sizes (in vw): `x-small`, `small`, `medium`, `wide`, `x-wide`, `xx-wide`, `xxx-wide`
 * - `fill` is an available size that should only be used once per Treegrid to fill in the leftover space.
 * @param {Array<string>} headings - An optional list of headings for the top of the table, to be displayed in order. For gaps between columns, leave empty strings.
 * @param {React.ReactNode} children - The contents inside the timeline. These should be of type `(yet to be made)`.
 * @param {string} className - CSS classes to apply to the timeline wrapper.
 */
class Treegrid extends React.Component<TreegridProps> {
    static propTypes = {
        isCompact: PropTypes.bool,
        columnSizes: PropTypes.arrayOf(PropTypes.oneOf(["16", "24", "32", "40", "48", "x-small", "small", "medium", "wide", "x-wide", "xx-wide", "xxx-wide", "fill"])),
        headings: PropTypes.arrayOf(PropTypes.string),
        children: PropTypes.node,
        className: PropTypes.string,
    }

    static defaultProps = {
        isCompact: false,
        columnSizes: [],
        headings: [],
        className: "",
    }

    render() {
        const { isCompact, columnSizes, headings, children, className, ...rest } = this.props;

        let classes = `o-treegrid`
        if (isCompact) classes += ` o-treegrid--compact`
        if (className) classes += ` ${className}`

        return (
            <table className={classes}>
                <colgroup className='o-treegrid__colgroup'>
                    {columnSizes.map((column) => {
                        return (
                            <col className={`o-treegrid__col o-treegrid__col--${column}`} span={1} />
                        );
                    })}
                </colgroup>
                {headings && 
                    <thead>
                        <tr className='o-treegrid__row o-treegrid__row--shown'>
                            {headings.map((heading) => {
                                return (
                                    <th className='o-treegrid__cell o-treegrid__cell--header'>{heading}</th>
                                )
                            })}
                        </tr>
                    </thead>
                }
                <tbody>
                    {children}
                </tbody>
            </table>
        );
    }
}

export default Treegrid;